import React, { useEffect, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material'
import { WindowContainer } from './WindowContainer'
import clsx from 'clsx'

const Container = styled(WindowContainer)(({ theme }) => ({
    zIndex: 4,
}))

const OverlayButton = styled('a')(({ theme }) => ({
    position: 'absolute',
    width: '90vw',
    left: '5vw',
    maxHeight: '180px',
    color: 'white',
    bottom: 'calc(7.2rem + 30px)',
    textAlign: 'center',
    textDecoration: 'none',
    visibility: 'hidden',
    transition: 'all 0.5s',
    zIndex: 5,
    [theme.breakpoints.up('md')]: {
        width: '240px',
        left: 'calc((100vw - 240px) / 2)',
        height: 'calc(240px * 0.22)',
    },
    '&.ButtonOut': {
        opacity: 0,
        visibility: 'hidden',
    },
    '&.ButtonIn': {
        opacity: 1, 
        visibility: 'visible',
    },
    '& > img': {
        width: '100%',
        height: 'auto',
        maxHeight: '180px',
        objectFit: 'contain',
    },
}))

function Overlay({ hidden, button, video }) {
    const second = useMemo(() => {
        if (!button || !button.timing) return 0

        const segments = button.timing.split(':')
        const min = parseInt(segments[0])
        const sec = parseInt(segments[1])

        if (isNaN(min) || isNaN(sec)) return 0

        return min * 60 + sec
    }, [button])

    const [visible, setVisible] = useState(!!button && second === 0)
    const hasShownRef = useRef(false)
    const hideTimerRef = useRef(null)

    // Reset when target is lost/found (button prop changes)
    useEffect(() => {
        hasShownRef.current = false
        clearTimeout(hideTimerRef.current)
        setVisible(!!button && second === 0)
    }, [button, second])

    // Stable listener — registered once, no stale closures
    useEffect(() => {
        if (!video || second <= 0) return

        const handleTimeUpdate = () => {
            const current = video.currentTime || 0

            // Natural loop detection — video rewound past timing mark
            if (hasShownRef.current && current < second) {
                hasShownRef.current = false
            }

            // Show when timing mark is hit
            if (!hasShownRef.current && current >= second) {
                hasShownRef.current = true
                setVisible(true)
                clearTimeout(hideTimerRef.current)
                hideTimerRef.current = setTimeout(() => setVisible(false), 10000)
            }
        }

        video.addEventListener('timeupdate', handleTimeUpdate)
        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate)
            clearTimeout(hideTimerRef.current)
        }
    }, [video, second])

    return (
        <Container sx={hidden ? { visibility: 'hidden' } : undefined}>
            {button && (
                <OverlayButton
                    href={button.url}
                    title={button.text}
                    className={clsx({
                        ButtonOut: !visible,
                        ButtonIn: !!visible,
                    })}
                >
                    <img
                        src={button.image ?? '/default-button.png'}
                        alt={button.text}
                    />
                </OverlayButton>
            )}
        </Container>
    )
}

export { Overlay }
